/**
 * generateDiamondSeriesPdf
 * Generates a styled, colourful Diamond Series (Chenni Paper) PDF report.
 *
 * Uses jsPDF + jspdf-autotable for crisp vector text and precise colour control.
 */
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// ─── Colour palette ───────────────────────────────────────────────────────────
const C = {
  navy:        [15,  23,  42],   // #0f172a  – page header bg
  blue:        [30,  64, 175],   // #1e40af  – section header bg
  blueLight:   [59, 130, 246],   // #3b82f6  – accent
  skyBg:       [219, 234, 254],  // #dbeafe  – alternate row
  white:       [255, 255, 255],
  offWhite:    [248, 250, 252],  // #f8fafc
  grayBg:      [241, 245, 249],  // #f1f5f9  – info box bg
  grayText:    [100, 116, 139],  // #64748b
  darkText:    [15,  23,  42],   // #0f172a
  successGreen:[5,   150, 105],  // #059669  – grand total accent
  tealAccent:  [20, 184, 166],   // #14b8a6  – tag pill
};

function formatDate(str) {
  if (!str) return '';
  const d = new Date(str.includes('T') ? str : str + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function setFillDraw(doc, fill, draw) {
  if (fill) doc.setFillColor(...fill);
  if (draw) doc.setDrawColor(...draw);
}

function tag(doc, x, y, text, bgColor, textColor, fontSize = 7) {
  doc.setFontSize(fontSize);
  const w = doc.getTextWidth(text) + 6;
  const h = 5.5;
  doc.setFillColor(...bgColor);
  doc.roundedRect(x, y - h + 1.5, w, h, 1.5, 1.5, 'F');
  doc.setTextColor(...textColor);
  doc.text(text, x + 3, y, { baseline: 'bottom' });
  return w + 2; // advance x
}

export function generateDiamondSeriesPdf(series) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw = 210; // page width
  const ml = 14;  // margin left
  const mr = 14;  // margin right
  const cw = pw - ml - mr; // content width
  let y = 0;

  // ── 1. Cover header band ───────────────────────────────────────────────────
  // Navy top band
  doc.setFillColor(...C.navy);
  doc.rect(0, 0, pw, 28, 'F');

  // Blue accent strip bottom of band
  doc.setFillColor(...C.blueLight);
  doc.rect(0, 26, pw, 2, 'F');

  // Title text
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...C.white);
  doc.text('DIAMOND SERIES', ml, 13, { baseline: 'middle' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(148, 163, 184); // slate-400
  doc.text('Chenni / Sieve Paper', ml, 20, { baseline: 'middle' });

  // Series number top-right
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...C.white);
  const snLabel = series.series_number || '';
  doc.text(snLabel, pw - mr, 12, { baseline: 'middle', align: 'right' });

  if (series.manual_series_no) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(`#${series.manual_series_no}`, pw - mr, 20, { baseline: 'middle', align: 'right' });
  }

  y = 32;

  // ── 2. Info card ────────────────────────────────────────────────────────────
  const infoH = 28;
  doc.setFillColor(...C.grayBg);
  doc.roundedRect(ml, y, cw, infoH, 2, 2, 'F');
  doc.setDrawColor(...C.skyBg);
  doc.roundedRect(ml, y, cw, infoH, 2, 2, 'S');

  const col1x = ml + 5;
  const col2x = ml + cw / 2 + 4;
  const rowH = 6;
  let iy = y + 7;

  const infoItem = (label, value, x, yy) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.grayText);
    doc.text(label, x, yy - 1);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...C.darkText);
    doc.text(String(value), x, yy + 4);
  };

  infoItem('COLOUR', series.colour || '—', col1x, iy);
  infoItem('SHADE', series.shade || '—', col2x, iy);
  iy += rowH + 3;
  infoItem('SHAPE', series.shape_type_name || '—', col1x, iy);
  infoItem('DATE', formatDate(series.date_of_issue), col2x, iy);
  iy += rowH + 3;

  // Clarity tags
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...C.grayText);
  doc.text('CLARITY', col1x, iy - 1);
  let tx = col1x;
  (series.clarity || []).forEach(c => {
    tx += tag(doc, tx, iy + 4, c, C.blueLight, C.white, 7);
  });

  // Codes used
  if (series.codes_used) {
    doc.setFontSize(7);
    doc.setTextColor(...C.grayText);
    doc.text('CODES USED', col2x, iy - 1);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...C.darkText);
    doc.text(series.codes_used, col2x, iy + 4);
  }

  // Submitted by
  if (series.submitted_by_name) {
    iy += rowH + 1;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.grayText);
    doc.text(`Submitted by: ${series.submitted_by_name}`, col1x, iy + 4);
  }

  y = y + infoH + 6;

  // ── 3. Category tables ──────────────────────────────────────────────────────
  (series.categories || []).forEach((cat, ci) => {
    const catWt = Number(cat.total_weight_ct ?? cat.sizes?.reduce((s, r) => s + (r.weight_ct || 0), 0) ?? 0);
    const catPcs = Math.round(cat.total_pieces ?? cat.sizes?.reduce((s, r) => s + (r.pieces || 0), 0) ?? 0);

    // Ensure there's room for at least the header + 1 data row (if near page bottom)
    if (y > 240) {
      doc.addPage();
      y = 14;
    }

    // Category header bar
    const headerBarH = 8;
    doc.setFillColor(...C.blue);
    doc.roundedRect(ml, y, cw, headerBarH, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...C.white);
    doc.text(cat.category_name || `Category ${ci + 1}`, ml + 5, y + headerBarH / 2, { baseline: 'middle' });

    // Category totals right-aligned
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const totStr = `${catWt.toFixed(3)} ct   ${catPcs.toLocaleString()} pcs`;
    doc.text(totStr, pw - mr - 3, y + headerBarH / 2, { baseline: 'middle', align: 'right' });

    y += headerBarH + 1;

    // Rows
    const rows = (cat.sizes || []).map((s, i) => [
      s.sieve_size_label || '',
      Number(s.avg_weight_ct).toFixed(3),
      Number(s.weight_ct).toFixed(3),
      Math.round(s.pieces).toLocaleString(),
    ]);

    doc.autoTable({
      startY: y,
      margin: { left: ml, right: mr },
      tableWidth: cw,
      head: [[
        { content: 'Sieve Size', styles: { halign: 'left' } },
        { content: 'Avg Wt (ct)', styles: { halign: 'right' } },
        { content: 'Weight (ct)', styles: { halign: 'right' } },
        { content: 'Pieces', styles: { halign: 'right' } },
      ]],
      body: rows,
      foot: [[
        { content: 'Category Total', colSpan: 2, styles: { halign: 'left', fontStyle: 'bold' } },
        { content: catWt.toFixed(3), styles: { halign: 'right', fontStyle: 'bold' } },
        { content: catPcs.toLocaleString(), styles: { halign: 'right', fontStyle: 'bold' } },
      ]],
      showFoot: 'lastPage',
      styles: {
        fontSize: 8.5,
        cellPadding: { top: 2.5, right: 4, bottom: 2.5, left: 4 },
        lineColor: [226, 232, 240],
        lineWidth: 0.2,
        textColor: C.darkText,
      },
      headStyles: {
        fillColor: [219, 234, 254],    // skyBg
        textColor: C.blue,
        fontStyle: 'bold',
        fontSize: 8,
      },
      footStyles: {
        fillColor: [219, 234, 254],
        textColor: C.blue,
        lineWidth: 0,
      },
      alternateRowStyles: {
        fillColor: C.offWhite,
      },
      bodyStyles: {
        fillColor: C.white,
      },
      columnStyles: {
        0: { halign: 'left',  cellWidth: 50 },
        1: { halign: 'right', cellWidth: 36 },
        2: { halign: 'right', cellWidth: 36 },
        3: { halign: 'right', cellWidth: 'auto' },
      },
      didParseCell: (data) => {
        if (data.section === 'foot') {
          data.cell.styles.fillColor = [219, 234, 254];
        }
      },
    });

    y = doc.lastAutoTable.finalY + 5;
  });

  // ── 4. Grand Total card ─────────────────────────────────────────────────────
  if (y > 255) { doc.addPage(); y = 14; }

  const gtH = 20;
  doc.setFillColor(...C.navy);
  doc.roundedRect(ml, y, cw, gtH, 2, 2, 'F');

  // Left label
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(148, 163, 184);
  doc.text('GRAND TOTAL', ml + 6, y + 7.5, { baseline: 'middle' });

  // Carats
  const gtW = Number(series.grand_total_weight_ct).toFixed(3);
  const gtP = Math.round(series.grand_total_pieces).toLocaleString();

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...C.white);
  doc.text(gtW, ml + 6, y + 15, { baseline: 'middle' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text('carats', ml + 6 + doc.getTextWidth(gtW) + 2, y + 15.5, { baseline: 'middle' });

  // Pieces
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...C.white);
  const piecesX = ml + 60;
  doc.text(gtP, piecesX, y + 15, { baseline: 'middle' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(148, 163, 184);
  doc.text('pieces', piecesX + doc.getTextWidth(gtP) + 2, y + 15.5, { baseline: 'middle' });

  // Categories count tag (right)
  const catCount = `${(series.categories || []).length} categories`;
  const ctW = doc.getTextWidth(catCount) + 8;
  doc.setFillColor(...C.blue);
  doc.roundedRect(pw - mr - ctW - 2, y + 6, ctW, 7, 2, 2, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...C.white);
  doc.text(catCount, pw - mr - ctW / 2 - 2, y + 9.5, { baseline: 'middle', align: 'center' });

  y += gtH + 8;

  // ── 5. Footer ───────────────────────────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    const footY = 287;
    doc.setDrawColor(...C.skyBg);
    doc.setLineWidth(0.3);
    doc.line(ml, footY - 3, pw - mr, footY - 3);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...C.grayText);
    doc.text(`Generated ${new Date().toLocaleDateString('en-IN')}  |  DiaForge`, ml, footY);
    doc.text(`${p} / ${totalPages}`, pw - mr, footY, { align: 'right' });
    if (series.series_number) {
      doc.text(series.series_number, pw / 2, footY, { align: 'center' });
    }
  }

  // ── Save ────────────────────────────────────────────────────────────────────
  const filename = `${series.series_number || 'diamond-series'}_${series.date_of_issue || 'report'}.pdf`;
  doc.save(filename);
}
